import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { appointmentService } from '../../services/appointmentService';
import { useLanguage } from '../../hooks/useLanguage';
import { AppointmentViewer } from './AppointmentViewer';
import { Appointment, DayState, dayStates, interventionLabel, todayKey } from './appointmentUtils';

const REFRESH_MS = 2 * 60 * 1000;

const chip: Record<DayState, string> = {
    done: 'bg-gray-100 text-gray-500',
    ongoing: 'bg-green-100 text-green-700',
    next: 'bg-indigo-600 text-white',
    upcoming: 'bg-white text-gray-500 border border-gray-200'
};

// Widget flottant (bas droite) : « Aujourd'hui · N RDV » avec l'état de chaque rendez-vous du jour
export const TodayAppointmentsWidget: React.FC = () => {
    const { t, isRTL } = useLanguage();
    const navigate = useNavigate();
    const [items, setItems] = useState<Appointment[]>([]);
    const [pending, setPending] = useState(0);
    const [loaded, setLoaded] = useState(false);
    const [expanded, setExpanded] = useState(typeof window === 'undefined' ? true : window.innerWidth >= 640);
    const [selected, setSelected] = useState<Appointment | null>(null);

    const load = useCallback(async () => {
        try {
            const [day, stats] = await Promise.all([
                appointmentService.getAppointments({ date: todayKey(), status: 'CONFIRMED,COMPLETED', limit: 50 }),
                appointmentService.getStats()
            ]);
            setItems((day.data || []).sort((a: Appointment, b: Appointment) => a.timeSlot.localeCompare(b.timeSlot)));
            setPending(stats.data?.awaitingGarage || 0);
        } catch {
            // le widget ne doit jamais gêner le dashboard
        } finally {
            setLoaded(true);
        }
    }, []);

    useEffect(() => {
        load();
        const timer = setInterval(load, REFRESH_MS);
        return () => clearInterval(timer);
    }, [load]);

    if (!loaded) return null;
    const states = dayStates(items);

    return (
        <>
            <div className={`fixed bottom-3 ${isRTL ? 'left-3' : 'right-3'} z-40 w-[19.5rem] max-w-[calc(100vw-1.5rem)] bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden`} dir={isRTL ? 'rtl' : 'ltr'}>
                <div className="flex items-center justify-between gap-2 px-4 py-3">
                    <button onClick={() => setExpanded(e => !e)} aria-expanded={expanded} className="flex items-center gap-1.5 font-bold text-gray-900 text-sm text-start">
                        {t('appointments.widget.title', { count: items.length })}
                        {expanded ? <ChevronDown size={16} className="text-gray-400" /> : <ChevronUp size={16} className="text-gray-400" />}
                    </button>
                    <button onClick={() => navigate('/dashboard/appointments')} className="text-sm font-semibold text-indigo-600 hover:underline">{t('appointments.widget.agenda')}</button>
                </div>
                {expanded && (
                    <>
                        {pending > 0 && (
                            <button onClick={() => navigate('/dashboard/appointments')} className="w-full text-start px-4 py-2 text-xs font-semibold text-amber-800 bg-amber-50 border-y border-amber-100 hover:bg-amber-100">
                                {t('appointments.widget.toValidate', { count: pending })}
                            </button>
                        )}
                        {items.length === 0 ? (
                            <p className="px-4 pb-4 pt-1 text-sm text-gray-500">{t('appointments.widget.empty')}</p>
                        ) : (
                            <ul className="max-h-72 overflow-y-auto border-t border-gray-100 divide-y divide-gray-100">
                                {items.map(a => {
                                    const st = states[a._id];
                                    return (
                                        <li key={a._id}>
                                            <button onClick={() => setSelected(a)} className={`w-full text-start px-4 py-2.5 flex items-center gap-3 hover:bg-gray-50 ${st === 'next' ? 'bg-indigo-50' : ''}`}>
                                                <span className={`w-11 text-sm font-bold ${st === 'next' ? 'text-indigo-600' : 'text-gray-500'}`}>{a.timeSlot}</span>
                                                <span className="flex-1 min-w-0">
                                                    <span className="block text-sm font-bold text-gray-900 truncate">{a.clientName}{a.vehicleModel ? ` · ${a.vehicleModel}` : ''}</span>
                                                    <span className="block text-xs text-gray-500 truncate">{interventionLabel(a, t(`appointments.type.${a.type}`, { defaultValue: a.type }))}</span>
                                                </span>
                                                <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap ${chip[st]}`}>{t(`appointments.widget.state.${st}`)}</span>
                                            </button>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </>
                )}
            </div>
            {selected && <AppointmentViewer appointment={selected} onClose={() => setSelected(null)} onChanged={load} />}
        </>
    );
};
